import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrashAlt, FaShoppingCart, FaLock } from 'react-icons/fa';

function CartPage({ cartItems, updateCartQuantity, removeFromCart, updateCartItemOptions }) {
  const navigate = useNavigate();

  const total = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  const handleSizeChange = (itemId, size) => {
    updateCartItemOptions(itemId, 'selectedSize', size);
  };

  const handleColorChange = (itemId, color) => {
    updateCartItemOptions(itemId, 'selectedColor', color);
  };

  const handleProceedToCheckout = () => {
    navigate('/checkout', { state: { cartItems: cartItems } });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FaShoppingCart className="text-orange-500" />
            Shopping Cart
          </h1>
          <p className="text-gray-600 mt-2">
            {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
          </p>
        </div>

        {cartItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-16 text-center">
            <FaShoppingCart className="mx-auto text-gray-300 text-6xl mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">Your cart is empty</h2>
            <p className="text-gray-500 mb-6">Add some products to get started!</p>
            <button 
              onClick={() => navigate('/')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items Section */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
                >
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full sm:w-40 h-40 object-cover rounded-lg border border-gray-200"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-grow space-y-3">
                      <div className="flex justify-between items-start gap-4">
                        <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                          {item.name}
                        </h3>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-500 hover:text-red-700 transition-colors flex-shrink-0 p-2 hover:bg-red-50 rounded-lg"
                          aria-label="Remove item"
                        >
                          <FaTrashAlt className="text-xl" />
                        </button>
                      </div>

                      <div className="text-2xl font-bold text-orange-600">
                        ৳{item.price.toLocaleString()}
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-medium">
                          In Stock
                        </span>
                        <span className="text-gray-500">
                          Eligible for FREE Shipping
                        </span>
                      </div>

                      {/* Size Selection */}
                      {item.isSizeAvailable && item.sizes && item.sizes.length > 0 && (
                        <div className="flex items-center gap-3">
                          <label className="text-sm font-semibold text-gray-700">
                            Size:
                          </label>
                          <select
                            value={item.selectedSize || ''}
                            onChange={(e) => handleSizeChange(item.id, e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white hover:border-gray-400 transition-colors"
                          >
                            <option value="" disabled>Select size</option>
                            {item.sizes.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Color Selection */}
                      {item.isColorAvailable && item.colors && item.colors.length > 0 && (
                        <div className="flex items-center gap-3">
                          <label className="text-sm font-semibold text-gray-700">
                            Color:
                          </label>
                          <div className="flex gap-2">
                            {item.colors.map((color) => (
                              <button
                                key={color.name}
                                onClick={() => handleColorChange(item.id, color)}
                                className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                                  item.selectedColor?.name === color.name
                                    ? 'border-orange-500 ring-2 ring-orange-200'
                                    : 'border-gray-300 hover:border-gray-400'
                                }`}
                                style={{ backgroundColor: color.hex }}
                                aria-label={color.name}
                                title={color.name}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quantity Control */}
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-semibold text-gray-700">
                          Quantity:
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <button
                            onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors font-semibold"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateCartQuantity(item.id, parseInt(e.target.value) || 1)}
                            min="1"
                            className="w-16 text-center border-x border-gray-300 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <button
                            onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors font-semibold"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b border-gray-200">
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal ({cartItems.length} items)</span>
                    <span className="font-semibold">৳{total.toLocaleString()}</span>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-lg font-bold text-gray-900">
                      <span>Order Total</span>
                      <span className="text-orange-600">৳{total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleProceedToCheckout}
                  disabled={cartItems.length === 0}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-4 rounded-lg font-bold text-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <FaLock className="text-sm" />
                  Proceed to Checkout
                </button>

                <div className="mt-6 space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Free delivery on orders over ৳500</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Secure checkout with SSL encryption</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Easy returns within 30 days</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CartPage;